import * as THREE from "three";

// Încadrarea camerei („zoom to fit", Stefan): scena se măsoară
// DUPĂ ce e construită, iar camera se așază la distanța minimă la care toată
// cutia ei intră în cadru. Direcția de privire rămâne cea aleasă de fiecare
// viewer — se schimbă doar ținta și distanța, deci perspectiva gândită
// pentru element (grinda văzută ușor de sus, stâlpul din colț) supraviețuiește.
//
// Distanța NU se ia din sfera circumscrisă cutiei: la un element alungit
// (grindă lungă și subțire) sfera e dominată de lungime, elementul ieșea mic,
// cu aer degeaba pe verticală («e loc de zoom», Stefan). În loc de asta,
// fiecare colț al cutiei se proiectează în reperul camerei și se cere să
// încapă pe AMBELE axe ale cadrului — three.js dă doar FOV-ul VERTICAL, iar
// în coloana îngustă din dreapta paginii limita reală e cea ORIZONTALĂ.
// Ținta: elementul să ocupe ~90 % din suprafața
// view-ului, oricât de mare sau mic ar fi el — marja implicită e deci 1/0,9.
export function incadreaza(
  camera: THREE.PerspectiveCamera,
  controls: { target: THREE.Vector3; update(): void },
  /** ce se încadrează: un obiect (cu tot ce ține de el) sau direct cutia
   *  lui — viewerele care au și CONTEXT în scenă (stâlpul schițat de la
   *  capătul grinzii, vecina de alături) își măsoară singure piesa și dau
   *  cutia ei, altfel cadrul s-ar umple cu contextul. */
  scena: THREE.Object3D | THREE.Box3,
  marja = 1 / 0.9
) {
  const cutie = scena instanceof THREE.Box3
    ? scena.clone()
    : new THREE.Box3().setFromObject(scena);
  if (cutie.isEmpty()) return;
  const centru = cutie.getCenter(new THREE.Vector3());

  const dir = camera.position.clone().sub(controls.target);
  if (dir.lengthSq() < 1e-12) dir.set(1, -1, 1);
  dir.normalize();
  // reperul camerei: `dir` = de la centru spre cameră, `dreapta` și `sus`
  // completează cadrul; dacă privirea cade exact pe axa `up`, orice dreapta e
  // la fel de bună
  const dreapta = new THREE.Vector3().crossVectors(camera.up, dir);
  if (dreapta.lengthSq() < 1e-9) dreapta.set(1, 0, 0);
  dreapta.normalize();
  const sus = new THREE.Vector3().crossVectors(dir, dreapta).normalize();

  const fovV = (camera.fov * Math.PI) / 180;
  const tanV = Math.tan(fovV / 2);
  const tanH = tanV * camera.aspect;

  // colțul cel mai pretențios dictează distanța: el trebuie să încapă și pe
  // lat (tanH), și pe înalt (tanV), ținând cont cât de APROAPE de cameră e
  // (adâncimea lui pe direcția de privire se adună la distanța necesară)
  let dist = 0;
  const colt = new THREE.Vector3();
  for (let i = 0; i < 8; i++) {
    colt.set(
      i & 1 ? cutie.max.x : cutie.min.x,
      i & 2 ? cutie.max.y : cutie.min.y,
      i & 4 ? cutie.max.z : cutie.min.z
    ).sub(centru);
    const adancime = colt.dot(dir);
    dist = Math.max(
      dist,
      adancime + Math.abs(colt.dot(dreapta)) / tanH,
      adancime + Math.abs(colt.dot(sus)) / tanV
    );
  }
  dist = Math.max(dist * marja, 0.2);

  camera.position.copy(centru).addScaledVector(dir, dist);
  controls.target.copy(centru);
  // planurile de tăiere urmează distanța, altfel un fit foarte larg (clădirea
  // întreagă) ar reteza fața apropiată sau ar pierde fundalul la zoom
  camera.near = Math.max(dist / 1000, 0.001);
  camera.far = Math.max(dist * 50, camera.far);
  camera.updateProjectionMatrix();
  controls.update();
}

// Cutia PIESEI dintr-o scenă care are și context: tot ce e marcat
// `userData.context = true` (stâlpul schițat de la capătul grinzii, ciotul
// vecinei) rămâne pe ecran, dar nu intră în măsurătoare. Fără regula asta,
// cutia unui stâlp desenat cu 1,5 m peste înălțimea grinzii — cât să se vadă
// că el continuă — dicta încadrarea, iar piesa rămânea o dungă în cadru.
export function cutiaPiesei(scena: THREE.Object3D): THREE.Box3 {
  const cutie = new THREE.Box3();
  scena.traverse((o) => {
    if (o.userData.context) return;
    if ((o as THREE.Mesh).geometry) cutie.expandByObject(o);
  });
  return cutie;
}
